import { Suspense, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import wLogoIcon from '@/assets/want-w-icon.svg';
import { lazyWithRecovery } from '@/lib/lazyWithRecovery';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';

// Eagerly loaded main tabs
import HomePage from '@/pages/HomePage';
import SavesPage from '@/pages/SavesPage';

// Lazy loaded secondary tabs (using shared recovery utility)
const TryOnPage = lazyWithRecovery(() => import('@/pages/TryOnPage'));
const StudioPage = lazyWithRecovery(() => import('@/pages/StudioPage'));
const ChatPage = lazyWithRecovery(() => import('@/pages/ChatPage'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <img src={wLogoIcon} alt="Loading" className="h-10 w-10 animate-spin" />
    <span className="sr-only">Loading…</span>
  </div>
);

// In dev/preview mode, don't keep inactive lazy tabs mounted to avoid
// stale chunk crashes during frequent code updates.
const isDevOrPreview = import.meta.env.DEV || window.location.hostname.includes('preview');

/**
 * TabView Component
 *
 * In production: keeps visited tabs mounted for instant switching.
 * In dev/preview: only mounts the active tab to prevent stale chunk crashes.
 *
 * Each lazy tab is wrapped in RouteErrorBoundary so a chunk failure
 * shows recovery UI instead of crashing the entire app.
 */
export function TabView() {
  const location = useLocation();
  const currentPath = location.pathname;

  // Track which lazy tabs have been activated at least once (production only)
  const activated = useRef<Set<string>>(new Set());

  const isStudioActive =
    currentPath === '/app/studio' ||
    currentPath === '/app/studio/hairdresser' ||
    currentPath === '/app/studio/profile' ||
    currentPath === '/app/studio/celebrity' ||
    currentPath === '/app/studio/body-gym' ||
    currentPath === '/app/studio/my-looks';

  // Mark current tab as activated
  if (currentPath === '/app/try-on') activated.current.add('try-on');
  if (isStudioActive) activated.current.add('studio');
  if (currentPath === '/app/chat') activated.current.add('chat');

  // In dev/preview: only mount active tab. In production: keep visited tabs mounted.
  const isTryOnMounted = isDevOrPreview
    ? currentPath === '/app/try-on'
    : activated.current.has('try-on');
  const isStudioMounted = isDevOrPreview
    ? isStudioActive
    : activated.current.has('studio');
  const isChatMounted = isDevOrPreview
    ? currentPath === '/app/chat'
    : activated.current.has('chat');

  return (
    <div className="relative h-full">
      {/* Saves Tab - /app */}
      <div
        className="absolute inset-0 h-full transition-opacity duration-150"
        style={{
          opacity: currentPath === '/app' ? 1 : 0,
          pointerEvents: currentPath === '/app' ? 'auto' : 'none',
          visibility: currentPath === '/app' ? 'visible' : 'hidden',
        }}
      >
        <SavesPage />
      </div>

      {/* Try On Tab - /app/try-on */}
      {isTryOnMounted && (
        <div
          className="absolute inset-0 h-full transition-opacity duration-150"
          style={{
            opacity: currentPath === '/app/try-on' ? 1 : 0,
            pointerEvents: currentPath === '/app/try-on' ? 'auto' : 'none',
            visibility: currentPath === '/app/try-on' ? 'visible' : 'hidden',
          }}
        >
          <RouteErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <TryOnPage />
            </Suspense>
          </RouteErrorBoundary>
        </div>
      )}

      {/* Studio Tab */}
      {isStudioMounted && (
        <div
          className="absolute inset-0 h-full transition-opacity duration-150"
          style={{
            opacity: isStudioActive ? 1 : 0,
            pointerEvents: isStudioActive ? 'auto' : 'none',
            visibility: isStudioActive ? 'visible' : 'hidden',
          }}
        >
          <RouteErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <StudioPage />
            </Suspense>
          </RouteErrorBoundary>
        </div>
      )}

      {/* Polls Tab - /app/vote */}
      <div
        className="absolute inset-0 h-full transition-opacity duration-150"
        style={{
          opacity: currentPath === '/app/vote' ? 1 : 0,
          pointerEvents: currentPath === '/app/vote' ? 'auto' : 'none',
          visibility: currentPath === '/app/vote' ? 'visible' : 'hidden',
        }}
      >
        <HomePage />
      </div>

      {/* Chat Tab */}
      {isChatMounted && (
        <div
          className="absolute inset-0 h-full transition-opacity duration-150"
          style={{
            opacity: currentPath === '/app/chat' ? 1 : 0,
            pointerEvents: currentPath === '/app/chat' ? 'auto' : 'none',
            visibility: currentPath === '/app/chat' ? 'visible' : 'hidden',
          }}
        >
          <RouteErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <ChatPage />
            </Suspense>
          </RouteErrorBoundary>
        </div>
      )}
    </div>
  );
}
